'use client';

import { ChangeEvent, FC, FocusEvent, SyntheticEvent, useEffect, useState } from 'react';
import MaterialSlider, { SliderProps } from '@mui/material/Slider';

import { TextInput } from '@components/shared/form-elements/text-input/TextInput';
import { iOS } from '@lib/constants/environment';

import classes from './Slider.module.scss';

export interface ISliderProps extends Omit<SliderProps, 'onChange'> {
  withInputs?: boolean;
  onChange?: (value: number[]) => void;
  id?: string;
}

const Slider: FC<ISliderProps> = ({ withInputs, id, ...other }) => {
  const isIOS = iOS(typeof window !== 'undefined' ? window.navigator.userAgent : '');
  const min = Math.floor(other.min || 0);
  const max = Math.ceil(other.max || 1);
  const defaultValue = (other.value as number[]) || [min, max];
  const step =
    other.step !== undefined
      ? other.step
      : Math.floor(Math.max(1, Math.min(Math.abs(defaultValue[1] - defaultValue[0]) / 10, (max - min) / 20)));
  const [value, setValue] = useState(defaultValue);
  const [valueApplied, setValueApplied] = useState(defaultValue);

  const valueProp = other.value;

  const valueString = value && value.toString();
  const valuePropString = valueProp && valueProp.toString();
  useEffect(() => {
    if (valueProp && Array.isArray(valueProp) && valueString !== valuePropString) {
      setValue(valueProp);
    }
  }, [valuePropString, valueProp]);

  const handleChange = (event: Event | SyntheticEvent<Element, Event>, rangeValue: number | number[]): void => {
    if (event.type === 'mousedown' && isIOS) {
      return;
    }
    setValue(typeof rangeValue === 'number' ? [rangeValue] : rangeValue);
  };

  const handleRangeChanged = (event: Event | SyntheticEvent<Element, Event>, rangeValue: number | number[]): void => {
    if (event.type === 'mouseup' && isIOS) {
      return;
    }
    if (other.onChange) {
      other.onChange(typeof rangeValue === 'number' ? [rangeValue] : rangeValue);
    }
  };

  const setTempValue = (isMin?: boolean) => (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target && event.target.value;

    const valueComputed: number = +rawValue || -1;

    setValue(isMin ? [valueComputed, value[1]] : [value[0], valueComputed]);
  };

  const setMinValue = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
    let minValue = +(event.target && event.target.value) || min;
    if (!minValue && minValue !== 0) {
      minValue = min;
    }
    if (value[0] !== valueApplied[0]) {
      const newRange = [minValue, value[1]];
      setValueApplied(newRange);
      handleChange(event, newRange);
      handleRangeChanged(event, newRange);
    }
  };

  const setMaxValue = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
    let maxValue = +(event.target && event.target.value);
    if (!maxValue) {
      maxValue = max;
    }
    if (value[1] !== valueApplied[1]) {
      const newRange = [value[0], +maxValue];
      setValueApplied(newRange);
      handleChange(event, newRange);
      handleRangeChanged(event, newRange);
    }
  };

  const markValues = [min, max];
  if (!withInputs) {
    markValues.concat(...value);
  }

  const marks = other.marks
    ? other.marks
    : markValues
        .filter((v, i, a) => a.indexOf(v) === i)
        .map((price) => {
          return {
            value: price,
            label: `${price}`
          };
        });

  return (
    <div>
      <MaterialSlider
        {...other}
        classes={{
          root: classes.root,
          track: classes.track,
          rail: classes.rail,
          thumb: classes.thumb,
          active: classes.active,
          mark: classes.mark,
          markLabel: classes.markLabel
        }}
        valueLabelDisplay="off"
        value={value}
        marks={marks}
        onChange={handleChange}
        onChangeCommitted={handleRangeChanged}
        step={step}
      />
      {withInputs && (
        <div className={classes.container}>
          <TextInput
            min={min}
            max={value[1] - 1}
            label="Від"
            placeholder={`${min}`}
            value={value[0] < 0 ? '' : value[0]}
            onChange={setTempValue(true)}
            onBlur={setMinValue}
            id={id}
          />
          <TextInput
            max={max}
            min={value[0] + 1}
            label="До"
            placeholder={`${max}`}
            value={value[1] < 0 ? '' : value[1]}
            onChange={setTempValue()}
            onBlur={setMaxValue}
            id={id}
          />
        </div>
      )}
    </div>
  );
};

export default Slider;
